import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { apiService } from '../../services/apiService';
import { BasicDetails, CompanyDetails, LookingForDetails, CompanyIntroVision } from '../../types/onboarding';

interface OnboardingState {
  currentStep: number;
  basicDetails: Partial<BasicDetails>;
  companyDetails: Partial<CompanyDetails>;
  lookingForDetails: Partial<LookingForDetails>;
  companyIntroVision: Partial<CompanyIntroVision>;
  isLoading: boolean;
  error: string | null;
  isCompleted: boolean;
}

const initialState: OnboardingState = {
  currentStep: 1,
  basicDetails: {},
  companyDetails: {},
  lookingForDetails: {},
  companyIntroVision: {},
  isLoading: false,
  error: null,
  isCompleted: false,
};

// Async thunks for API calls
export const saveBasicDetails = createAsyncThunk(
  'onboarding/saveBasicDetails',
  async (data: BasicDetails, { rejectWithValue }) => {
    try {
      const response: any = await apiService.post('/onboarding/basic-details', data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to save basic details');
    }
  }
);

export const saveCompanyDetails = createAsyncThunk(
  'onboarding/saveCompanyDetails',
  async (data: CompanyDetails, { rejectWithValue }) => {
    try {
      const response: any = await apiService.post('/onboarding/company-details', data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to save company details');
    }
  }
);

export const saveLookingForDetails = createAsyncThunk(
  'onboarding/saveLookingForDetails',
  async (data: LookingForDetails, { rejectWithValue }) => {
    try {
      const response: any = await apiService.post('/onboarding/looking-for', data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to save preferences');
    }
  }
);

export const saveCompanyIntroVision = createAsyncThunk(
  'onboarding/saveCompanyIntroVision',
  async (data: CompanyIntroVision, { rejectWithValue }) => {
    try {
      const response: any = await apiService.post('/onboarding/company-intro', data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to save company intro');
    }
  }
);

export const completeOnboarding = createAsyncThunk(
  'onboarding/complete',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as any;
      const onboardingData = {
        basicDetails: state.onboarding.basicDetails,
        companyDetails: state.onboarding.companyDetails,
        lookingForDetails: state.onboarding.lookingForDetails,
        companyIntroVision: state.onboarding.companyIntroVision,
      };
      
      const response: any = await apiService.post('/onboarding/complete', onboardingData);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to complete onboarding');
    }
  }
);

const onboardingSlice = createSlice({
  name: 'onboarding',
  initialState,
  reducers: {
    setCurrentStep: (state, action: PayloadAction<number>) => {
      state.currentStep = action.payload;
    },
    updateBasicDetails: (state, action: PayloadAction<Partial<BasicDetails>>) => {
      state.basicDetails = { ...state.basicDetails, ...action.payload };
    },
    updateCompanyDetails: (state, action: PayloadAction<Partial<CompanyDetails>>) => {
      state.companyDetails = { ...state.companyDetails, ...action.payload };
    },
    updateLookingForDetails: (state, action: PayloadAction<Partial<LookingForDetails>>) => {
      state.lookingForDetails = { ...state.lookingForDetails, ...action.payload };
    },
    updateCompanyIntroVision: (state, action: PayloadAction<Partial<CompanyIntroVision>>) => {
      state.companyIntroVision = { ...state.companyIntroVision, ...action.payload };
    },
    nextStep: (state) => {
      state.currentStep += 1;
    },
    previousStep: (state) => {
      if (state.currentStep > 1) {
        state.currentStep -= 1;
      }
    },
    resetOnboarding: (state) => {
      return initialState;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Save Basic Details
      .addCase(saveBasicDetails.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(saveBasicDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.basicDetails = action.payload;
      })
      .addCase(saveBasicDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Save Company Details
      .addCase(saveCompanyDetails.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(saveCompanyDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.companyDetails = action.payload;
      })
      .addCase(saveCompanyDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Save Looking For Details
      .addCase(saveLookingForDetails.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(saveLookingForDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.lookingForDetails = action.payload;
      })
      .addCase(saveLookingForDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Save Company Intro Vision
      .addCase(saveCompanyIntroVision.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(saveCompanyIntroVision.fulfilled, (state, action) => {
        state.isLoading = false;
        state.companyIntroVision = action.payload;
      })
      .addCase(saveCompanyIntroVision.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Complete Onboarding
      .addCase(completeOnboarding.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(completeOnboarding.fulfilled, (state) => {
        state.isLoading = false;
        state.isCompleted = true;
      })
      .addCase(completeOnboarding.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setCurrentStep,
  updateBasicDetails,
  updateCompanyDetails,
  updateLookingForDetails,
  updateCompanyIntroVision,
  nextStep,
  previousStep,
  resetOnboarding,
  clearError,
} = onboardingSlice.actions;

export default onboardingSlice.reducer;